import { InstantSearch, Configure, Index } from 'react-instantsearch-dom'
import { connectStateResults, connectAutoComplete } from 'react-instantsearch/connectors'
import { Component, h } from 'preact'
import algoliasearch from 'algoliasearch/lite'
import globals from '../../../utils/globals'
import { SEARCH_TOKEN_URL } from '../../../libs/config'
import getWithInMemoryCache from '../../../utils/getWithInMemoryCache'
import AlgoliaSearch from './AlgoliaSearch'
import AlgoliaSearchForControl from './AlgoliaSearchForControl'
import { Hit } from 'js/src/preact-components/algolia/components'
import { SearchBox } from './SearchBox'

const SearchWithAutoComplete = connectAutoComplete(connectStateResults(AlgoliaSearch))
const SearchWithAutoCompleteForControl = connectAutoComplete(connectStateResults(AlgoliaSearchForControl))

declare global {
    interface Window {
      aa: (command: string, data: {
        applicationID: string,
        apiKey: string
      } | {
        objectID: string,
        position: number,
        queryID: string
      }) => void
    }
}

interface Props {
  isControl?: boolean,
  isFederatedSearch?: boolean,
  isFederatedSalesforceSearch?: boolean,
  isUserGuest?: boolean,
  searchPlaceholder?: string,
  callback?: (hit: Hit) => void,
  exclude?: Array<number>,
  submitIconType?: string
  category?:number,
  isParentCategory?:boolean,
  searchByContentType?: Array<string>
}

interface State {
  searching: boolean,
  searchById: boolean,
  searchByContentType?: Array<string>,
  finalInput: string,
  searchClient?: algoliasearch.Client
}

interface SearchQuery {
  q: string,
  contentType?: string
}

class Search extends Component<Props, State> {
  constructor (props: Props) {
    super(props)
    this.state = {
      searching: false,
      searchById: false,
      searchByContentType: undefined,
      finalInput: '',
      searchClient: undefined
    }
    this.updateSearch = this.updateSearch.bind(this)
  }

  updateSearch (searchTerm: SearchQuery, next: () => void) {
    this.setState({
      searching: searchTerm.q !== '',
      searchById: searchTerm.q.match(/^[0-9]+$/) !== null,
      finalInput: searchTerm.q,
      searchByContentType: searchTerm.contentType ? [searchTerm.contentType] : undefined
    }, next)
  }

  async componentDidMount () {
    const data: {client_id: string, token: string} = await getWithInMemoryCache(SEARCH_TOKEN_URL, {})
    const searchClient = algoliasearch(data.client_id, data.token)
    this.setState({
      searchClient: searchClient
    }, () => {
      if (typeof window.aa === 'function') {
        window.aa('init', {
          applicationID: data.client_id,
          apiKey: data.token
        })
      }
    })
  }

  render (props: Props, state: State) {
    if (!state.searchClient) {
      return <SearchBox
        submitIconType={props.submitIconType || ''}
      />
    }
    const communityId = globals('communityId')
    return <PureSearch
      searchClient={state.searchClient}
      updateSearch={this.updateSearch}
      searchById={state.searchById}
      searchByContentType={state.searchByContentType}
      finalInput={state.finalInput}
      categoryId={props.category}
      searching={state.searching}
      communityId={typeof communityId === 'string' ? communityId : ''}
      submitIconType={props.submitIconType || ''}
      {...props} />
  }
}

interface PureProps extends Props {
  communityId: string,
  searching: boolean,
  searchById: boolean,
  searchByContentType?: Array<string>,
  finalInput: string,
  searchClient: algoliasearch,
  updateSearch: (value: SearchQuery, cb: () => void) => void,
  categoryId?: number
}

const getFilters = (props: PureProps) => {

  if (props.categoryId) {
    if (props.isParentCategory) {
      return `category:${props.categoryId}`
    } else {
      return `forum:${props.categoryId}`
    }
  }

  if (props.searchByContentType) {
   return props.searchByContentType.map(value => {
      return `content_type:${value}`
    }).join(' OR ')
  }
  return undefined
}

const PureSearch = (props: PureProps) => <InstantSearch
  searchClient={props.searchClient}
  indexName={props.communityId}
>
  <Index indexName={props.communityId}>
    <Configure
      hitsPerPage={5}
      filters={getFilters(props)}
      clickAnalytics={props.searching}
      analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
      restrictSearchableAttributes={props.searchById ? ['id'] : ['title', 'first_post', 'answer', 'reply', 'tags', 'forum_title', 'category_title']} />
  </Index>

  {
    props.isFederatedSalesforceSearch ? (
      <Index indexName={`${props.communityId}-salesforce`}>
        <Configure
          hitsPerPage={3}
          filters={getFilters(props)}
          clickAnalytics={props.searching}
          analyticsTags={props.isControl ? ['control'] : ['destination', 'live']}
        />
      </Index>
    ) : null
  }

  {
    props.isFederatedSearch ? (
      <Index indexName={`${props.communityId}-zendesk`}>
        <Configure
          hitsPerPage={3}
          clickAnalytics={props.searching} />
      </Index>
    ) : null
  }

  {
    props.isControl ? (
      <SearchWithAutoCompleteForControl
        updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
        searchById={props.searchById}
        finalInput={props.finalInput}
        placeholder={props.searchPlaceholder}
        callback={props.callback}
        exclude={props.exclude}
        submitIconType={props.submitIconType || ''}
      />
    ) : (
      <SearchWithAutoComplete
        updateSearch={(value: SearchQuery, next: () => void) => props.updateSearch(value, next)}
        callback={props.callback}
        indexName={props.communityId}
        isFederatedSearch={props.isFederatedSearch}
        isFederatedSalesforceSearch={props.isFederatedSalesforceSearch}
      />
    )
  }
</InstantSearch>

export { PureSearch, SearchQuery }
export default Search
